/**
 * Logging utility for consistent debugging across the application
 */

const LOG_LEVELS = {
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR',
  DEBUG: 'DEBUG'
};

type LogLevel = typeof LOG_LEVELS[keyof typeof LOG_LEVELS];

interface LogOptions {
  includeTimestamp?: boolean;
  saveToStorage?: boolean;
  storageKey?: string;
  maxStoredLogs?: number;
}

const DEFAULT_OPTIONS: LogOptions = {
  includeTimestamp: true,
  saveToStorage: true, 
  storageKey: 'app_debug_logs',
  maxStoredLogs: 100
};

/**
 * Log message with timestamp and metadata
 */
export const logMessage = (
  module: string, 
  message: string, 
  level: LogLevel = LOG_LEVELS.INFO,
  data?: any,
  options: LogOptions = DEFAULT_OPTIONS
) => {
  const timestamp = options.includeTimestamp ? new Date().toISOString() : undefined;
  const logEntry = {
    timestamp,
    module,
    level,
    message,
    data: data || null
  };
  
  // Format for console
  const formattedMessage = `[${timestamp}] [${level}] [${module}] ${message}`;
  
  switch (level) {
    case LOG_LEVELS.ERROR:
      console.error(formattedMessage, data);
      break;
    case LOG_LEVELS.WARN:
      console.warn(formattedMessage, data);
      break;
    case LOG_LEVELS.DEBUG:
      console.debug(formattedMessage, data);
      break;
    default:
      console.log(formattedMessage, data);
  }
  
  // Save to localStorage if enabled
  if (options.saveToStorage) {
    saveLogToStorage(logEntry, options.storageKey, options.maxStoredLogs);
  }
  
  return logEntry;
};

/**
 * Save log entry to localStorage
 */
const saveLogToStorage = (
  logEntry: any, 
  storageKey: string = DEFAULT_OPTIONS.storageKey!, 
  maxLogs: number = DEFAULT_OPTIONS.maxStoredLogs!
) => {
  try {
    // Get existing logs
    const logsJson = localStorage.getItem(storageKey);
    let logs = logsJson ? JSON.parse(logsJson) : [];
    
    // Add new log entry
    logs.push(logEntry);
    
    // Keep only the most recent logs
    if (logs.length > maxLogs) {
      logs = logs.slice(logs.length - maxLogs);
    }
    
    // Save back to localStorage
    localStorage.setItem(storageKey, JSON.stringify(logs));
  } catch (error) {
    console.error('Failed to save log to localStorage:', error);
  }
};

/**
 * Clear stored logs
 */
export const clearLogs = (storageKey: string = DEFAULT_OPTIONS.storageKey!) => {
  try {
    localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.error('Failed to clear logs:', error);
    return false;
  }
};

/**
 * Get all stored logs
 */
export const getLogs = (storageKey: string = DEFAULT_OPTIONS.storageKey!) => {
  try {
    const logsJson = localStorage.getItem(storageKey);
    return logsJson ? JSON.parse(logsJson) : [];
  } catch (error) {
    console.error('Failed to get logs from localStorage:', error);
    return [];
  }
};

// Convenience methods
export const logInfo = (module: string, message: string, data?: any, options?: LogOptions) => 
  logMessage(module, message, LOG_LEVELS.INFO, data, options);

export const logWarning = (module: string, message: string, data?: any, options?: LogOptions) => 
  logMessage(module, message, LOG_LEVELS.WARN, data, options);

export const logError = (module: string, message: string, data?: any, options?: LogOptions) => 
  logMessage(module, message, LOG_LEVELS.ERROR, data, options);

export const logDebug = (module: string, message: string, data?: any, options?: LogOptions) => 
  logMessage(module, message, LOG_LEVELS.DEBUG, data, options);

export const LOG_LEVELS_ARRAY = Object.values(LOG_LEVELS);

export default { 
  logMessage, 
  logInfo, 
  logWarning, 
  logError,
  logDebug,
  getLogs,
  clearLogs,
  LOG_LEVELS,
  LOG_LEVELS_ARRAY
}; 